import { Icon } from "@studio3t/generic-components";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  jobsApi,
  schemasApi,
  servicesApi,
  testSuitesApi,
  workspacesApi,
} from "@/api/client";
import { useLeaveGuard, useRegisterUnsavedWork } from "@/components/LeaveGuard";
import { useToast } from "@/components/toast/ToastProvider";
import { queryKeys } from "@/hooks/useApi";
import {
  buildWorkspaceFromJob,
  findWorkspaceForJob,
  prefillInput,
} from "@/hooks/useImportJob";
import type { JobControlState } from "@/pages/pipelineControls";
import type {
  CreateJobRequest,
  JobMetadata,
  Service,
  Workspace,
} from "@/types/api";
import { buildPipelineCode, resolveCodeDraft } from "./pipelineCode";
import {
  editedSinceOpen,
  type OpenedSnapshot,
  unsavedWorkKind,
} from "./pipelineEdits";
import {
  collectPipelineRefs,
  fetchRefLookups,
  type PipelineDocError,
  refErrors,
  validatePipelineDoc,
} from "./pipelineLint";
import { stagingFromPipelineDoc } from "./pipelineStagesFromCode";
import {
  editsLockedFor,
  parseSchemaRowId,
  usePipelineStaging,
} from "./pipelineStaging";
import { PIPELINE_CODE_TEMPLATE } from "./pipelineTemplate";
import {
  buildWorkspacePayload,
  freeDuplicateName,
  scratchBaseName,
  storedFields,
} from "./saveWorkspace";
import {
  type SchemaRegistryEntry,
  schemaRefsToIds,
  schemaRefsToNames,
} from "./schemaRefs";
import {
  deployedJobFor,
  failureText,
  retireJob,
  samePipeline,
  stopExistingJob,
  useJobControls,
  viewOnlyFor,
} from "./studioPipelineJobs";
import { untitledScriptRowIds } from "./unsavedScripts";

// The pipeline the Studio header has OPEN, and every lifecycle action that
// moves between its states (Figma 14894-169764). Three states, one union:
// nothing open (scratch — null), a saved workspace open (draft), and a
// workspace whose pipeline is live as a job (deployed). A workspace is the
// unit of editing either way; deploying pins the job it produced on the
// workspace (deployed_job), and opening a job materializes the workspace
// behind it — the import recipe useImportJob runs for /pipelines.
//
// Everything here is state + async plumbing; the header components (the
// "Open pipeline" menu, the name group, the Deploy/Start/Stop group) read
// it through useStudioPipeline() and hold no lifecycle state of their own.

export type OpenPipeline =
  | { kind: "draft"; workspace: Workspace }
  | { kind: "deployed"; workspace: Workspace; job: JobMetadata };

/** What "Save" on a draft came back with: saved, or held because the job
 *  the draft still points at is running (retiring it needs a stop first). */
export type SaveOutcome =
  | { kind: "saved" }
  | { kind: "job-running"; jobName: string };

export interface StudioPipelineValue {
  openPipeline: OpenPipeline | null;
  /** Badge + button convergence for the deployed state (null otherwise). */
  jobControl: JobControlState | null;
  /** Open a saved workspace; `name` titles the unsaved-work switch
   *  modal ("Open <name>") when the guard holds the move. */
  openDraft: (target: { workspaceId: string; name?: string }) => Promise<void>;
  openJob: (jobName: string) => Promise<void>;
  /** "Save as draft" (scratch): create workspace `name`, enter draft
   *  state. Returns the created workspace so save-then-deploy (scratch
   *  Deploy) can pass it straight to deployOpenDraft without reading
   *  stale state from its closure. */
  saveAsDraft: (name: string) => Promise<Workspace>;
  /** "Save" (draft state): overwrite the open workspace. A draft born
   *  from a stopped/failed pipeline also retires that job — unless it is
   *  running again, which comes back as "job-running" for the header to
   *  ask about; `stopJob` is the answer. */
  saveOpenDraft: (options?: { stopJob?: boolean }) => Promise<SaveOutcome>;
  /** "Clear and start fresh": pristine studio, back to scratch state. */
  clearToScratch: () => void;
  /** Deploy the open draft; `ws` overrides the state read for the
   *  scratch save-then-deploy chain (state set by saveAsDraft hasn't
   *  re-rendered the caller yet). */
  deployOpenDraft: (ws?: Workspace) => Promise<void>;
  /** A deploy is in flight — the Deploy button disables on it, the way
   *  jobControl's start/stopDisabled cover start and stop. */
  deploying: boolean;
  /** "Duplicate as draft" from the deployed state. */
  duplicateAsDraft: (name: string) => Promise<void>;
  stopPipeline: () => Promise<void>;
  startPipeline: () => Promise<void>;
  /** View-only studio: the open deployed pipeline runs or converges, so
   *  every edit surface locks (editsLockedFor) — viewing, selection and
   *  the lifecycle actions stay. */
  editsLocked: boolean;
  /** The open pipeline has been edited since it was opened (or last
   *  saved). On a deployed pipeline those edits are LOCAL to the browser
   *  — the pipeline stays deployed and nothing is written until an action
   *  says so; the header stars the name on this flag. */
  openEdited: boolean;
  /** A pipeline is on its way in (openDraft / openJob fetching): the code
   *  editor holds its empty-state affordances until it lands. True from
   *  the first frame when the page arrives with ?workspace. */
  opening: boolean;
  /** The toolbar's "Stages" click: hands an edited code draft over to the
   *  staged lists (pipelineStagesFromCode) — straight away when nothing is
   *  lost, otherwise it comes back for the modal to ask (docs/
   *  stages-code-round-trip.md). A plain switch when the code wasn't
   *  edited or the studio is view-only. An exactly-empty document (the
   *  user cleared the pipeline) switches to empty Stages rather than
   *  asking — there's no code to fix. */
  prepareStagesSwitch: () => Promise<StagesSwitch>;
  /** Deploy/stop/start error surfaced to the header (dismissable). */
  actionError: string | null;
  dismissActionError: () => void;
}

/** The outcome of prepareStagesSwitch. "switched" is done; "errors" needs
 *  the user (the toolbar's modal): the code has lint errors — syntax,
 *  structure or references, the same checks the editor's linter runs.
 *  Placeholder entries are not errors: they hand over as nothing staged. */
export type StagesSwitch = { kind: "switched" } | { kind: "errors" };

const StudioPipelineContext = createContext<StudioPipelineValue | null>(null);

// ── Editor state ──────────────────────────────────────────

/** The current editor content as a workspace payload for the given name —
 *  with the pipeline's schema refs stored by registry ID (schemaRefs.ts);
 *  a name the registry can't settle rejects, so nothing ambiguous is ever
 *  written. */
function useWorkspacePayload(
  currentPipelineCode: ReturnType<typeof useCurrentPipelineCode>,
) {
  const { inputDocument, schemas } = usePipelineStaging();

  return async (name: string) => {
    const { code } = currentPipelineCode();
    const resolved = schemaRefsToIds(code, await loadSchemaRegistry(code), {
      preferred: stagedSchemaIds(schemas),
    });
    if (resolved.errors.length > 0) throw new Error(resolved.errors.join("; "));
    return buildWorkspacePayload({
      name,
      inputDocument,
      pipelineCode: resolved.code,
    });
  };
}

/** The pipeline code the editor currently holds (`code`) — the draft when
 *  it still matches its generated base (the code the save and the deploy
 *  both write), the freshly generated code otherwise — plus that base
 *  (`generated`), which deploy re-pins its name-stamped draft on. */
function useCurrentPipelineCode(keepDraft: boolean) {
  const {
    sources,
    transforms,
    sinks,
    schemas,
    pipelineCodeDraft,
    pipelineExtras,
  } = usePipelineStaging();

  return () => {
    const generated = buildPipelineCode(
      { sources, transforms, sinks, schemas },
      pipelineExtras,
    );
    return {
      code: resolveCodeDraft(pipelineCodeDraft, generated, keepDraft),
      generated,
    };
  };
}

// Tests live in the shared catalog, and the Tests panel stages ONE of its
// suites. A workspace therefore stores a REFERENCE to that suite in
// test_suite_id — no snapshot suite is ever written — and reopening
// re-stages the suite live from the catalog, so edits made anywhere reach
// every workspace pointing at it.

// ── The pipeline document ─────────────────────────────────

/** The pipeline code as a document, or null when it isn't JSON at all. */
function parseJson(code: string): unknown {
  try {
    return JSON.parse(code);
  } catch {
    return null;
  }
}

const serviceNameOf = (entry: unknown) =>
  (entry as { service_name?: unknown })?.service_name;

const isNamed = (name: unknown): name is string =>
  typeof name === "string" && name !== "";

// The services a pipeline doc's schemas block names — each entry's
// service_name, once, ignoring anything unnamed.
function schemaServiceNames(doc: unknown): string[] {
  const schemas = (doc as { schemas?: unknown } | null)?.schemas;
  if (!Array.isArray(schemas)) return [];
  return [...new Set(schemas.map(serviceNameOf).filter(isNamed))];
}

// The registry entries a pipeline doc's schema refs can translate against
// (schemaRefs.ts): the lists of every service its schemas block names,
// each entry tagged with its service (schemas live per service). A list
// that can't be fetched contributes nothing — the refs it would have
// translated simply stay as they are.
async function loadSchemaRegistry(
  code: string,
): Promise<SchemaRegistryEntry[]> {
  const lists = await Promise.all(
    schemaServiceNames(parseJson(code)).map(async (serviceName) => {
      const entries = await schemasApi.list(serviceName).catch(() => []);
      return entries.map((e) => ({ ...e, serviceName }));
    }),
  );
  return lists.flat();
}

// The staged schemas' "<service>:<name>" → registry IDs in staged order:
// settles a schemas-block name that several of that service's entries
// share (schemaRefsToIds' `preferred`). Names are unique only within one
// service's store, so the key carries the service; two staged schemas
// sharing both become two block entries (buildSchemasBlock, in this same
// order), each settling to its own id. Only schemas a stage references
// make it into the block, so only those count here.
function stagedSchemaIds(
  schemas: ReadonlyArray<{
    id: string;
    name: string;
    attachments: readonly unknown[];
  }>,
): Map<string, string[]> {
  const ids = new Map<string, string[]>();
  for (const s of schemas) {
    if (s.attachments.length === 0) continue;
    const owner = parseSchemaRowId(s.id);
    if (!owner) continue;
    const key = `${owner.serviceName}:${s.name}`;
    ids.set(key, [...(ids.get(key) ?? []), owner.entryId]);
  }
  return ids;
}

// The explorer's leaf icon per provider; anything else is a collection.
const PROVIDER_ROW_ICONS = new Map<Service["provider"] | undefined, Icon>([
  ["udf", Icon.Code],
  ["http", Icon.Link],
  ["http_source", Icon.Link],
  ["generator", Icon.Lightning],
]);

// The row icons the derived staging wears (pipelineStagesFromCode takes
// them from its caller): the explorer's leaf icons by provider.
const DERIVED_ROW_ICONS = {
  resource: (provider: Service["provider"] | undefined): Icon =>
    PROVIDER_ROW_ICONS.get(provider) ?? Icon.Collection,
  schema: Icon.Schema,
};

// The services list a derivation resolves row providers from, awaited so a
// row never stages with its provider missing just because the list was
// still loading: the provider drives the generated code's encoding
// defaults, the Deploy gate and the "Source delivers" control, which must
// all see the same thing. The cached list when there is one, one fetch
// otherwise; an unreachable engine yields no providers at all, which every
// consumer treats alike (nothing fixed, nothing gated, nothing shown).
function useServicesForDerivation(): () => Promise<Service[]> {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    try {
      return await queryClient.ensureQueryData({
        queryKey: queryKeys.services({}),
        queryFn: () => servicesApi.list({}),
      });
    } catch {
      return [];
    }
  }, [queryClient]);
}

// The staged rows a pipeline's code derives to (view-only studio), or the
// one issue that stops it: code that isn't JSON. The parser's own issues
// (entries it skipped) ride along as messages.
function deriveStages(
  code: string,
  services: ReadonlyArray<{ name: string; provider: Service["provider"] }>,
  registry: SchemaRegistryEntry[],
): {
  doc: unknown;
  staging: ReturnType<typeof stagingFromPipelineDoc>;
  issues: string[];
} {
  let doc: unknown;
  try {
    doc = JSON.parse(code);
  } catch (e) {
    const empty = stagingFromPipelineDoc(null, {
      services,
      registry,
      icons: DERIVED_ROW_ICONS,
    });
    return {
      doc: null,
      staging: empty,
      issues: [`The pipeline code isn't valid JSON: ${failureText(e)}`],
    };
  }
  const staging = stagingFromPipelineDoc(doc, {
    services,
    registry,
    icons: DERIVED_ROW_ICONS,
  });
  return { doc, staging, issues: staging.issues.map((i) => i.message) };
}

// The linter's reference checks (services, resources, schemas the engine
// knows) for a parsed doc — async, TTL-cached like the code view's lint.
async function referenceErrors(doc: unknown): Promise<PipelineDocError[]> {
  const refs = collectPipelineRefs(doc);
  return refErrors(refs, await fetchRefLookups(refs));
}

// Placeholder entries (their warnings) don't block: untouched in the code,
// they are simply empty in Stages — the parser stages nothing for them.
// Only errors do.
const isBlockingError = (e: PipelineDocError) => e.severity !== "warning";

// Whether the code has any lint error that stops the hand-over to Stages —
// structure (the entries the parser would have to skip) or a reference the
// engine can't resolve.
async function stagesBlocked(doc: unknown): Promise<boolean> {
  if (validatePipelineDoc(doc).some(isBlockingError)) return true;
  return (await referenceErrors(doc)).length > 0;
}

// ── Opening a pipeline ────────────────────────────────────

// The pipeline code the editor shows for an open pipeline. A deployed
// pipeline shows the JOB's config — what actually runs (id-based schema
// refs and all) — not the workspace's stored copy, which can drift from it
// (a schema renamed after the save turns its name-based ref stale while
// the job keeps running on the id). Same serialization as importing a job
// (buildWorkspaceFromJob), so the two paths can't disagree on formatting.
// A job without a readable config falls back to the workspace copy; a
// workspace with none opens on the template (the same document empty
// staging generates).
export function pipelineCodeFor(
  ws: Workspace,
  job: JobMetadata | null,
): string {
  if (job?.pipeline) return JSON.stringify(job.pipeline, null, 2);
  return ws.pipeline ?? PIPELINE_CODE_TEMPLATE;
}

// The generated base a loaded pipeline's code draft pins on: the code the
// derived staging generates for a viewed pipeline, the code empty staging
// generates for anything else — the view-only pin keeps the draft on
// screen either way (useCurrentPipelineCode's keepDraft).
function generatedBase(
  derived: ReturnType<typeof deriveStages> | null,
): string {
  if (!derived) {
    return buildPipelineCode({
      sources: [],
      transforms: [],
      sinks: [],
      schemas: [],
    });
  }
  return buildPipelineCode(derived.staging, derived.staging.extras);
}

// The suite a workspace references, re-staged live from the catalog. The
// caller's clearPipeline() already dropped the previously staged group.
// Legacy: a workspace whose test_suite_id points at an old snapshot suite
// simply stages that suite as the group — acceptable; the tree hides
// snapshot suites, so its row just shows no in-use badge.
async function stageStoredSuite(
  ws: Workspace,
  stageSuiteGroup: ReturnType<typeof usePipelineStaging>["stageSuiteGroup"],
) {
  if (!ws.test_suite_id) return;
  try {
    const suite = await testSuitesApi.get(ws.test_suite_id);
    if (suite) stageSuiteGroup(suite, Icon.Test);
  } catch {
    // The referenced suite may be gone — the workspace still opens.
  }
}

// Opening a workspace into the editor: pristine staging, its pipeline as
// the code draft (based on the template the empty staging generates — the
// job's config when the pipeline is deployed, see pipelineCodeFor), its
// input in the editor, its referenced suite re-staged on the Tests panel,
// the graph restarted on it, and the Full pipeline view up. The open-state
// bookkeeping is the caller's — this only touches the editor.
function useLoadWorkspaceIntoEditor() {
  const {
    clearPipeline,
    setPipelineCodeDraft,
    setInputDocument,
    resetGraphView,
    setStudioView,
    stageSuiteGroup,
    loadStagedSections,
    setCodeIssues,
    setPipelineExtras,
  } = usePipelineStaging();
  const loadServices = useServicesForDerivation();

  return async (ws: Workspace, job: JobMetadata | null) => {
    const stored = pipelineCodeFor(ws, job);
    // Schema refs read by name in the editor (schemaRefs.ts).
    const registry = await loadSchemaRegistry(stored);
    const pipeline = schemaRefsToNames(stored, registry);
    // A running job is view-only: its code also becomes staged rows
    // (pipelineStagesFromCode) so the Stages view can show it, with the
    // code draft pinned on the generated base. Anything else opens as
    // before: code draft over empty staging.
    const derived = viewOnlyFor(job)
      ? deriveStages(pipeline, await loadServices(), registry)
      : null;
    // The clear puts the view on Stages › Source; a pipeline that opens on
    // its code takes the view back in the same tick, so the empty Stages
    // never paint in between (they did, for the rest of this load).
    clearPipeline();
    if (!derived) setStudioView("full");
    if (derived) {
      loadStagedSections(derived.staging);
      setPipelineExtras(derived.staging.extras);
      setCodeIssues(derived.issues);
    }
    setPipelineCodeDraft({
      text: pipeline,
      basedOn: generatedBase(derived),
    });
    setInputDocument(ws.input ?? "");
    await stageStoredSuite(ws, stageSuiteGroup);
    resetGraphView(pipeline);
    return openedSnapshot(pipeline, ws);
  };
}

/** What the studio was loaded with — the baseline "edited since open"
 *  compares against (pipelineEdits.ts). */
function openedSnapshot(code: string, ws: Workspace): OpenedSnapshot {
  return { code, input: ws.input ?? "", suiteId: ws.test_suite_id ?? null };
}

// ?workspace=<id> (the Drafts tab / "Open in Studio") loads that
// workspace on arrival, then drops the param so a reload or copied URL
// doesn't re-import over later edits. Mirrors TransformStudio's loader.
function useWorkspaceParamLoader(loadWorkspace: (id: string) => Promise<void>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");
  const lastWorkspaceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!workspaceParam) return;
    if (lastWorkspaceRef.current === workspaceParam) return;
    lastWorkspaceRef.current = workspaceParam;
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("workspace");
    setSearchParams(nextParams, { replace: true });
    loadWorkspace(workspaceParam).catch(() => {
      // An unknown/unreachable workspace leaves the studio at point zero.
    });
  }, [workspaceParam, searchParams, setSearchParams, loadWorkspace]);
}

// ── Deploying ─────────────────────────────────────────────

/** The workspace a deploy writes: the caller's override — the scratch
 *  save-then-deploy chain, whose saveAsDraft state hasn't reached the
 *  action's closure yet — else the open draft. Nothing else deploys. */
function deployTargetFor(
  wsOverride: Workspace | undefined,
  openPipeline: OpenPipeline | null,
): Workspace | null {
  if (wsOverride) return wsOverride;
  if (openPipeline?.kind !== "draft") return null;
  return openPipeline.workspace;
}

/** The document a deploy sends, or the one thing that stops it before any
 *  call is made: a schema ref the registry can't settle. */
type DeployDoc =
  | { kind: "blocked"; reason: string }
  | {
      kind: "ok";
      doc: Record<string, unknown>;
      code: string;
      registry: SchemaRegistryEntry[];
    };

// The editor's code as the document to deploy: schema refs out by registry
// ID (schemaRefs.ts) — an ambiguous name is blocked here rather than
// deploying a guess — and the job's name stamped on it.
//
// The job takes the WORKSPACE's name, not the `name` the code carries (the
// template's "my-pipeline" until someone edits it by hand): the name the
// user typed is the pipeline's identity everywhere else — the "Open
// pipeline" menu pairs a workspace with its job by name, and two drafts
// deployed under the template name would collide on one job.
//
// Deploy is only reachable when the code-view lint has settled clean
// (lintBlockReason gating lives in the header), so JSON.parse throwing is
// the defensive branch, not the normal path.
async function prepareDeployDoc(options: {
  code: string;
  jobName: string;
  preferred: ReadonlyMap<string, readonly string[]>;
}): Promise<DeployDoc> {
  const { code, jobName, preferred } = options;
  const registry = await loadSchemaRegistry(code);
  const resolved = schemaRefsToIds(code, registry, { preferred });
  if (resolved.errors.length > 0) {
    return { kind: "blocked", reason: resolved.errors.join("; ") };
  }
  const doc = JSON.parse(resolved.code) as Record<string, unknown>;
  const deployedDoc = { ...doc, name: jobName };
  return {
    kind: "ok",
    doc: deployedDoc,
    code: JSON.stringify(deployedDoc, null, 2),
    registry,
  };
}

/** Whether a job under the workspace's name exists; `undefined` when the
 *  list could not be read, which proves nothing either way. */
async function jobExistsFor(
  workspace: Workspace,
): Promise<boolean | undefined> {
  try {
    return (await jobsApi.list()).some((j) => j.name === workspace.name);
  } catch {
    return undefined;
  }
}

// POST /jobs answers with a Message envelope whose item is optional, so
// the deployed job is read back off the list — the badge needs real
// state/desired_state from the start.
async function readBackJob(
  workspace: Workspace,
  created: JobMetadata | undefined,
): Promise<JobMetadata> {
  const jobName = workspace.name;
  const job = (await jobsApi.list()).find((j) => j.name === jobName) ?? created;
  if (!job) throw new Error(`Deployed pipeline "${jobName}" not found`);
  return job;
}

// The menu and Clear stay live through a deploy, so the pipeline open by
// the time it lands may not be the one deployed — enter the deployed state
// only if it still is. A functional update sees the state saveAsDraft
// queued moments earlier, so the scratch chain (save → deploy) matches on
// the draft it just created rather than on the null it started from.
function enterDeployed(
  prev: OpenPipeline | null,
  target: Workspace,
  deployed: { workspace: Workspace; job: JobMetadata },
): OpenPipeline | null {
  if (prev === null) return prev;
  if (prev.workspace.id !== target.id) return prev;
  return { kind: "deployed", ...deployed };
}

// ── The provider ──────────────────────────────────────────

export function StudioPipelineProvider({ children }: { children: ReactNode }) {
  const {
    clearPipeline,
    stagedSuite,
    inputDocument,
    hasUnsavedWork,
    setPipelineCodeDraft,
    schemas,
    pipelineCodeDraft,
    setStudioView,
    loadStagedSections,
    setPipelineExtras,
    dropPipelineCodeDraft,
    setCodeIssues,
    setSavedInput,
    transforms,
    discardScriptDraft,
    unstage,
  } = usePipelineStaging();
  const loadServices = useServicesForDerivation();
  const [openPipeline, setOpenPipeline] = useState<OpenPipeline | null>(null);
  // Seeded from the URL so the editor never paints its ghost template
  // before the ?workspace load even starts.
  const [arrivalParams] = useSearchParams();
  const [opening, setOpening] = useState(() => arrivalParams.has("workspace"));
  // What the open pipeline looked like when it was opened (or deployed) —
  // the baseline "edited since open" compares against.
  const [opened, setOpened] = useState<OpenedSnapshot | null>(null);
  // opened and the staging layer's savedInput move together: the snapshot
  // drives "edited since open", its input is the badge/reselect baseline
  // (pipelineStaging.savedInput) — one recorder so they never drift.
  const recordOpened = useCallback(
    (snap: OpenedSnapshot | null) => {
      setOpened(snap);
      setSavedInput(snap?.input ?? "");
    },
    [setSavedInput],
  );
  const [actionError, setActionError] = useState<string | null>(null);
  // Deploy is a multi-call round trip (create job → read it back → update
  // the workspace): the flag disables the button, and the ref rejects the
  // second click that lands before the re-render — a second create under
  // the same name would fail or fork the pipeline.
  const [deploying, setDeploying] = useState(false);
  const deployInFlight = useRef(false);
  const queryClient = useQueryClient();

  const dismissActionError = useCallback(() => setActionError(null), []);

  const { jobControl, startPipeline, stopPipeline, pollUntilSettled } =
    useJobControls(openPipeline, setOpenPipeline, setActionError);
  // View-only studio (a running job): the code draft is pinned as-is —
  // it IS the deployed code — and every edit surface locks on this flag.
  const editsLocked = editsLockedFor(
    openPipeline?.kind ?? null,
    jobControl?.displayState ?? null,
  );

  const currentPipelineCode = useCurrentPipelineCode(editsLocked);
  const payloadFor = useWorkspacePayload(currentPipelineCode);
  const loadWorkspaceIntoEditor = useLoadWorkspaceIntoEditor();
  const currentSnapshot = (): OpenedSnapshot => ({
    code: currentPipelineCode().code,
    input: inputDocument,
    suiteId: stagedSuite?.suiteId ?? null,
  });
  // Edits never change the open pipeline's state: a stopped/failed
  // deployed pipeline stays deployed, its edits local to the browser
  // (TTTL-203) — starred in the header until saved elsewhere or dropped.
  const openEdited =
    !editsLocked &&
    opened !== null &&
    editedSinceOpen(currentSnapshot(), opened);
  // The leave guard: an open pipeline edited since open/save, or scratch
  // work, holds every way out of the studio behind the unsaved-changes
  // modal.
  const leaveGuard = useLeaveGuard();
  const unsavedKind = unsavedWorkKind(
    openPipeline?.kind ?? null,
    openEdited,
    hasUnsavedWork,
  );

  const derivationContext = async (registry: SchemaRegistryEntry[]) => ({
    services: await loadServices(),
    registry,
    icons: DERIVED_ROW_ICONS,
  });

  // The hand-over itself: the derived rows become the staged lists (and
  // the passthrough extras), the draft goes — the code view then shows the
  // regenerated code — and Stages comes up.
  const stageDerived = (derived: ReturnType<typeof stagingFromPipelineDoc>) => {
    loadStagedSections(derived);
    setPipelineExtras(derived.extras);
    dropPipelineCodeDraft();
    setCodeIssues([]);
    setStudioView("stages");
  };

  // An edited code draft hands over to the staged lists. Code with any
  // lint error — syntax, structure (the entries the parser would have to
  // skip) or a reference the engine can't resolve — can't switch
  // ("errors"): the user fixes it in Full first, so the hand-over is
  // always lossless and the staged rows never carry problems of their own.
  const handOverCode = async (code: string): Promise<StagesSwitch> => {
    // An exactly-empty document has no diagnostics (lint status "empty"),
    // so the "fix the code errors" modal would be wrong — treat it as the
    // user having emptied the pipeline and switch to empty Stages.
    // Whitespace-only text is a real syntax error and falls through below.
    if (code === "") {
      stageDerived(
        stagingFromPipelineDoc(null, {
          services: [],
          registry: [],
          icons: DERIVED_ROW_ICONS,
        }),
      );
      return { kind: "switched" };
    }
    const doc = parseJson(code);
    if (doc === null || (await stagesBlocked(doc))) return { kind: "errors" };
    const registry = await loadSchemaRegistry(code);
    stageDerived(
      stagingFromPipelineDoc(doc, await derivationContext(registry)),
    );
    return { kind: "switched" };
  };

  // Full → Stages: a plain switch when the code wasn't edited or the
  // studio is view-only, the hand-over above otherwise.
  const prepareStagesSwitch = async (): Promise<StagesSwitch> => {
    const { code, generated } = currentPipelineCode();
    const edited = pipelineCodeDraft !== null && code !== generated;
    if (editsLocked || !edited) {
      setStudioView("stages");
      return { kind: "switched" };
    }
    return handOverCode(code);
  };

  // Every workspace write here happens outside React Query, so the cached
  // list (useWorkspaces — the Drafts tab and everything else reading
  // it) is refreshed by hand; same deal as the jobs cache after a deploy.
  // The "Open pipeline" menu doesn't ride on this — it fetches both lists
  // itself each time it opens.
  const refreshWorkspaces = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.workspaces }),
    [queryClient],
  );

  // Opening a saved workspace. Its deployed_job decides the state: a job
  // still under that name means the workspace IS the live pipeline.
  const openWorkspace = async (workspaceId: string) => {
    // A workspace opened by id is a draft in all but the rare deployed case,
    // and a draft opens on its code: switch now, so the fetches below run
    // behind the code view instead of the empty stages (a running job's
    // load moves the view to Stages when it lands).
    setStudioView("full");
    setOpening(true);
    try {
      const ws = await workspacesApi.get(workspaceId);
      // The job decides what the editor shows (pipelineCodeFor), so it
      // resolves before the load.
      const job = await deployedJobFor(ws);
      recordOpened(await loadWorkspaceIntoEditor(ws, job));
      setOpenPipeline(
        job
          ? { kind: "deployed", workspace: ws, job }
          : { kind: "draft", workspace: ws },
      );
    } catch (e) {
      setActionError(`Open failed: ${failureText(e)}`);
    } finally {
      setOpening(false);
    }
  };

  useWorkspaceParamLoader(openWorkspace);
  // The studio's own ways out of the current work go through the leave
  // guard too; arriving by ?workspace (above) has nothing to leave. A
  // switch carries the target's name so the guard can show the switch
  // modal ("Open <name>") instead of the generic unsaved-changes one.
  const openDraft = async (target: { workspaceId: string; name?: string }) => {
    leaveGuard.confirm(
      () => void openWorkspace(target.workspaceId),
      target.name,
    );
  };

  // The workspace behind a deployed job, or — for a job deployed outside
  // the studio — one materialized from the job's own pipeline (useImportJob's
  // recipe, minus the navigation it does for /pipelines).
  const workspaceForJob = async (job: JobMetadata): Promise<Workspace> => {
    const existing = await findWorkspaceForJob(job.name);
    if (existing) return existing;
    const payload = buildWorkspaceFromJob(job);
    if (payload.input_origin) {
      payload.input = await prefillInput(payload.input_origin);
    }
    const created = await workspacesApi.create(payload);
    refreshWorkspaces();
    return created;
  };

  // Opening a deployed job.
  const openDeployedJob = async (jobName: string) => {
    setOpening(true);
    try {
      const job = (await jobsApi.list()).find((j) => j.name === jobName);
      if (!job) throw new Error(`Pipeline "${jobName}" is not deployed`);
      const workspace = await workspaceForJob(job);
      recordOpened(await loadWorkspaceIntoEditor(workspace, job));
      setOpenPipeline({ kind: "deployed", workspace, job });
    } catch (e) {
      setActionError(`Open failed: ${failureText(e)}`);
    } finally {
      setOpening(false);
    }
  };
  const openJob = async (jobName: string) => {
    leaveGuard.confirm(() => void openDeployedJob(jobName), jobName);
  };

  // A new workspace from the current editor state, pointing at whichever
  // catalog suite is staged (none staged stores no link).
  const saveAsDraft = async (name: string): Promise<Workspace> => {
    const created = await workspacesApi.create({
      ...(await payloadFor(name)),
      test_suite_id: stagedSuite?.suiteId ?? null,
    });
    refreshWorkspaces();
    // The created draft holds exactly what the editor shows — a duplicate
    // carries the local edits it was taken with — so the editor state is
    // the new "edited since" baseline: no asterisk until the user edits
    // on top of it.
    recordOpened(openedSnapshot(currentPipelineCode().code, created));
    setOpenPipeline({ kind: "draft", workspace: created });
    return created;
  };

  // "Save" on an open draft: overwrite it with the current editor state
  // under its existing name, staged suite reference included. A draft
  // born from a stopped/failed pipeline still points at that job: the
  // save retires it (retireJob) — from then on the draft IS the pipeline,
  // off the Pipelines list until Deploy recreates the job. A job that
  // started running in the meantime is only stopped on the header's say-so.
  const saveOpenDraft = async (
    options: { stopJob?: boolean } = {},
  ): Promise<SaveOutcome> => {
    if (openPipeline?.kind !== "draft") return { kind: "saved" };
    const ws = openPipeline.workspace;
    if (ws.deployed_job) {
      const job = await retireJob(ws.deployed_job, options.stopJob ?? false);
      if (job === "running")
        return { kind: "job-running", jobName: ws.deployed_job };
      queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    }
    const updated = await workspacesApi.update(ws.id, {
      ...(await payloadFor(ws.name)),
      test_suite_id: stagedSuite?.suiteId ?? null,
      deployed_job: null,
    });
    refreshWorkspaces();
    // The saved state is the new baseline for "edited since" (the leave
    // guard and the header's asterisk both read it).
    recordOpened(openedSnapshot(currentPipelineCode().code, updated));
    setOpenPipeline({ kind: "draft", workspace: updated });
    return { kind: "saved" };
  };

  // "Duplicate as draft" off a deployed pipeline — how you edit a live
  // pipeline: the copy carries the CURRENT editor content — the job's
  // running config (pipelineCodeFor), not the stored workspace copy — and
  // opens as the draft, leaving the job alone.
  const duplicateAsDraft = async (name: string) => {
    await saveAsDraft(name);
  };

  // The deploy round trip: the editor's pipeline code becomes a job, and
  // the workspace records it (deployed_job) so reopening lands in the
  // deployed state. The stamped code (prepareDeployDoc) is what gets
  // deployed, stored AND put back in the editor, so the Full pipeline view
  // can't disagree with the running job.
  const deployWorkspace = async (workspace: Workspace) => {
    const { code, generated } = currentPipelineCode();
    const jobName = workspace.name;
    const prepared = await prepareDeployDoc({
      code,
      jobName,
      preferred: stagedSchemaIds(schemas),
    });
    if (prepared.kind === "blocked") throw new Error(prepared.reason);
    // Pinned on the code the staging generates right now, the way the
    // code editor pins its own edits — a later staging change drops it
    // and regenerates, same as any other hand edit. The editor keeps
    // reading the refs by name.
    const deployedText = schemaRefsToNames(prepared.code, prepared.registry);
    setPipelineCodeDraft({ text: deployedText, basedOn: generated });
    // A workspace redeployed (or reopened while its job lives on) must
    // not race the job it replaces: stop it first.
    await stopExistingJob(workspace.deployed_job);
    // Read before the create, so a job found after a failed one is known to
    // be this deploy's rather than one that held the name already.
    const existedBefore = await jobExistsFor(workspace);
    let created: Awaited<ReturnType<typeof jobsApi.create>>;
    try {
      created = await jobsApi.create(
        prepared.doc as unknown as CreateJobRequest,
      );
    } catch (e) {
      // A failed create can still leave this deploy's job behind: a change
      // capture pipeline whose first start fails is kept, stopped and
      // failed, as it may hold its replication slot, and a lost reply hides
      // a create that went through. Recorded as this workspace's job, it
      // opens with its state and a Restart, rather than as a draft whose
      // next deploy only meets the name taken. Only a job that did not hold
      // the name before, and runs exactly the pipeline sent, is adopted.
      if (existedBefore === false) {
        await recordDeployed({
          workspace,
          code: prepared.code,
          deployedText,
          mustMatch: prepared.doc as unknown as CreateJobRequest,
        }).catch(() => undefined);
      }
      throw e;
    }
    // An untitled script row has no file and dropped out of the code just
    // deployed (TTTL-254): it goes the way its minus takes it, so the
    // deployed pipeline shows only what it runs. The header's warning modal
    // said as much before the press; a blank untitled row goes quietly.
    for (const id of untitledScriptRowIds(transforms)) {
      discardScriptDraft(id);
      unstage(id);
    }
    await recordDeployed({
      workspace,
      code: prepared.code,
      deployedText,
      created: created.item,
    });
  };

  // The job a deploy left, recorded on its workspace and opened: read back
  // for its real state, so the badge shows it from the start.
  const recordDeployed = async (deploy: {
    workspace: Workspace;
    code: string;
    deployedText: string;
    created?: JobMetadata;
    mustMatch?: CreateJobRequest;
  }) => {
    const { workspace, code, deployedText } = deploy;
    // Deploy writes outside React Query — refresh the jobs cache so the
    // Pipelines table shows the new job immediately.
    queryClient.invalidateQueries({ queryKey: queryKeys.jobs });
    const job = await readBackJob(workspace, deploy.created);
    // A job found after a failed create is adopted only when it is the one
    // this deploy sent.
    if (deploy.mustMatch && !samePipeline(job, deploy.mustMatch)) return;
    // storedFields carries the server-side content (scripts, input,
    // schema, test_suite_id, pipeline) across the update, same as a
    // rename does; the code just deployed replaces the stored pipeline.
    const updated = await workspacesApi.update(workspace.id, {
      ...storedFields(workspace),
      name: workspace.name,
      pipeline: code,
      deployed_job: job.name,
    });
    refreshWorkspaces();
    // The deployed pipeline is what the editor now shows — the new
    // baseline any later local edits are starred against.
    recordOpened(openedSnapshot(deployedText, updated));
    setOpenPipeline((prev) =>
      enterDeployed(prev, workspace, { workspace: updated, job }),
    );
    // A fresh job is stopped/starting against a desired Running — the
    // same convergence poll start/stop use carries the badge from
    // "Starting" to "Running". A poll for a job that is no longer the
    // open one ends itself (useJobControls abandons it on the switch).
    pollUntilSettled(job.name);
  };

  // Deploy the open draft; `wsOverride` serves the scratch chain —
  // saveAsDraft's state hasn't reached this closure yet when Deploy
  // follows the save.
  const deployOpenDraft = async (wsOverride?: Workspace) => {
    const workspace = deployTargetFor(wsOverride, openPipeline);
    if (!workspace || deployInFlight.current) return;
    deployInFlight.current = true;
    setDeploying(true);
    try {
      await deployWorkspace(workspace);
    } catch (e) {
      setActionError(`Deploy failed: ${failureText(e)}`);
    } finally {
      deployInFlight.current = false;
      setDeploying(false);
    }
  };

  // "Clear and start fresh": the pristine studio, nothing open. A stale
  // error belonged to the pipeline just closed, so it goes too.
  const clearToScratch = useCallback(() => {
    leaveGuard.confirm(() => {
      clearPipeline();
      setOpenPipeline(null);
      recordOpened(null);
      setActionError(null);
    });
  }, [clearPipeline, leaveGuard.confirm, recordOpened]);

  // The current editor content saved as a FRESH draft under the first
  // free name — no prompt in the way of leaving — announced on a toast so
  // the name is known. The guard's "Duplicate and continue" for a deployed
  // pipeline's local edits: its copy suffixes the pipeline's name (-CLONE,
  // -CLONE-2, …). Scratch work is asked for a name instead (saveAs below),
  // so the scratch fallback here only covers the type.
  const { toast } = useToast();
  const saveWorkAsNewDraft = async (): Promise<string | null> => {
    const base =
      openPipeline?.workspace.name ??
      scratchBaseName(currentPipelineCode().code);
    const [workspaces, jobs] = await Promise.all([
      workspacesApi.list(),
      jobsApi.list(),
    ]);
    const name = freeDuplicateName(base, [...workspaces, ...jobs]);
    await saveAsDraft(name);
    toast({
      state: "Confirm",
      message: `Your changes were saved as the draft "${name}"`,
    });
    return null;
  };
  useRegisterUnsavedWork(
    unsavedKind === null
      ? null
      : {
          kind: unsavedKind,
          name: openPipeline?.workspace.name ?? null,
          // A draft's Save is the header's Save (over itself). A draft
          // whose retired job is running again can't be saved from the
          // modal.
          save:
            unsavedKind === "draft"
              ? async () => {
                  const outcome = await saveOpenDraft();
                  return outcome.kind === "saved"
                    ? null
                    : `"${outcome.jobName}" is running again — stop it from the Studio header to save, or leave without saving.`;
                }
              : null,
          // Scratch is saved under the name the guard's "Save your work as
          // a draft" prompt collects — the header's Save as draft, from
          // the modal.
          saveAs:
            unsavedKind === "scratch"
              ? async (name: string) => {
                  await saveAsDraft(name);
                  return null;
                }
              : null,
          // Deployed local edits can only be KEPT as a copy — the switch
          // modal's "Duplicate and continue".
          duplicate: unsavedKind === "deployed" ? saveWorkAsNewDraft : null,
        },
  );

  // Not memoized: the actions close over the live editor state, so the
  // value changes with every staging change anyway — and the header, its
  // only consumer, re-renders with that state regardless.
  const value: StudioPipelineValue = {
    openPipeline,
    jobControl,
    openDraft,
    openJob,
    saveAsDraft,
    saveOpenDraft,
    clearToScratch,
    deployOpenDraft,
    deploying,
    duplicateAsDraft,
    stopPipeline,
    startPipeline,
    editsLocked,
    openEdited,
    opening,
    prepareStagesSwitch,
    actionError,
    dismissActionError,
  };

  return (
    <StudioPipelineContext.Provider value={value}>
      {children}
    </StudioPipelineContext.Provider>
  );
}

export function useStudioPipeline(): StudioPipelineValue {
  const ctx = useContext(StudioPipelineContext);
  if (!ctx) {
    throw new Error(
      "useStudioPipeline must be used within StudioPipelineProvider",
    );
  }
  return ctx;
}
